import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ItemType } from "../../../types";
import "./ItemTypePicker.css";

export type ItemTypeOption = {
  id: ItemType;
  label: string;
  defaultIcon?: string;
};

export type PickerPosition = {
  top: number;
  left: number;
};

type ItemTypePickerProps = {
  itemTypes: ItemTypeOption[];
  isLoading?: boolean;
  onSelect: (itemType: ItemType) => void;
  onClose: () => void;
  position: PickerPosition;
};

export const ItemTypePicker: React.FC<ItemTypePickerProps> = ({
  itemTypes,
  isLoading = false,
  onSelect,
  onClose,
  position,
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleSelect = (itemType: ItemType) => {
    onSelect(itemType);
    onClose();
  };

  const pickerContent = (
    <div
      className="item-type-picker item-type-picker--portal"
      ref={pickerRef}
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="item-type-picker__header">Add new...</div>
      {isLoading ? (
        <div className="item-type-picker__loading">Loading...</div>
      ) : (
        <ul className="item-type-picker__list">
          {itemTypes.map((item) => (
            <li key={item.id}>
              <button
                className="item-type-picker__option"
                onClick={() => handleSelect(item.id)}
              >
                {item.defaultIcon && (
                  <span className="item-type-picker__icon">
                    {item.defaultIcon}
                  </span>
                )}
                <span className="item-type-picker__label">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return createPortal(pickerContent, document.body);
};
